'use client'

import { Button } from "@/components/ui/button";
import { Utils } from "@/lib/utils";
import { Check, Code, Copy } from 'lucide-react';
import { useTranslations } from "next-intl";
import { useState } from 'react';
import SectionPageLayout from "../layouts/section-page-layout";

const Widget = () => {
      const t = useTranslations('landing.widget');
      const [copied, setCopied] = useState(false);

      const integrationCode = `<script>
  (function(w,d,s,o,f,js,fjs){
    w['kiwilog']=o;w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments)};
    js=d.createElement(s),fjs=d.getElementsByTagName(s)[0];
    js.id=o;js.src=f;js.async=1;fjs.parentNode.insertBefore(js,fjs);
  }(window,document,'script','kw','https://widget.kiwilog.app/launcher.js'));
  
  kw('init', {
    appId: 'YOUR_APP_ID',
    position: 'bottom-right'
  });
</script>`;

      const copyToClipboard = () => {
            Utils.string.copyToClipboard(integrationCode)
                  .then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                  });
      };

      return (
            <SectionPageLayout id="widget">
                  <div className="text-center mb-16">
                        <div className="inline-block px-3 py-1 mb-6 rounded-full bg-kiwilog text-white text-sm font-medium">
                              {t('plug_and_play_widget')}
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">
                              {t('integrate_updates_directly_into_your_product')}
                        </h2>
                        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto text-balance">
                              {t('add_our_lightweight')}
                        </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
                        <div className="lg:col-span-3 order-2 lg:order-1">
                              <div className="bg-gray-900 rounded-xl p-4 shadow-xl animate-scale-in">
                                    <div className="flex items-center justify-between mb-4">
                                          <div className="flex space-x-2">
                                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                          </div>
                                          <Button
                                                size="sm"
                                                onClick={copyToClipboard}
                                                className="bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs py-1 px-3 rounded flex items-center transition-colors"
                                          >
                                                {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                                                {copied ? 'Copied!' : 'Copy'}
                                          </Button>
                                    </div>

                                    <pre className="text-gray-300 text-sm overflow-auto p-2 font-mono">
                                          <code>{integrationCode}</code>
                                    </pre>
                              </div>

                              <div className="glass-card dark:bg-gray-800 mt-8 p-6 border rounded-xl bg-white shadow-sm">
                                    <h3 className="text-xl font-semibold mb-4">{t('simple_integration_process')}</h3>
                                    <ol className="space-y-4">
                                          <li className="flex items-start">
                                                <div className="bg-primary-100 text-primary-500 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                                                      1
                                                </div>
                                                <p className="text-gray-700">
                                                      <span className="font-medium">{t('sign_up_and_create_a_project')}</span> - {t('get_your_unique_app_id_and_customize_your_widget_settings')}
                                                </p>
                                          </li>
                                          <li className="flex items-start">
                                                <div className="bg-primary-100 text-primary-500 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                                                      2
                                                </div>
                                                <p className="text-gray-700">
                                                      <span className="font-medium">{t('copy_the_code_snippet')}</span> - {t('paste_it_into_your_website_or_app_right_before_the_closing_body_tag')}
                                                </p>
                                          </li>
                                          <li className="flex items-start">
                                                <div className="bg-primary-100 text-primary-500 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                                                      3
                                                </div>
                                                <p className="text-gray-700">
                                                      <span className="font-medium">{t('publish_updates')}</span> - {t('create_and_publish_updates')}
                                                </p>
                                          </li>
                                    </ol>

                                    <div className="mt-6">
                                          <Button
                                                className="bg-kiwilog button-effect"
                                                size="lg"
                                          >
                                                <Code size={18} className="mr-2" />
                                                {t('get_your_widget_code')}
                                          </Button>
                                    </div>
                              </div>
                        </div>

                        <div className="lg:col-span-2 order-1 lg:order-2 flex justify-center">
                              {/* Widget Preview */}
                              <div className="relative animate-float">
                                    <div className="w-64 h-[500px] bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
                                          {/* Mock Website */}
                                          <div className="h-full bg-gray-50 p-3">
                                                <div className="h-8 bg-gray-200 rounded-md mb-4 animate-pulse" />
                                                <div className="h-24 bg-gray-200 rounded-md mb-4" />
                                                <div className="h-3 bg-gray-200 rounded-md mb-2 w-3/4" />
                                                <div className="h-3 bg-gray-200 rounded-md mb-2" />
                                                <div className="h-3 bg-gray-200 rounded-md mb-2 w-5/6" />
                                                <div className="h-3 bg-gray-200 rounded-md mb-6 w-2/3" />

                                                <div className="h-40 bg-gray-200 rounded-md mb-4" />
                                                <div className="h-3 bg-gray-200 rounded-md mb-2" />
                                                <div className="h-3 bg-gray-200 rounded-md mb-2 w-4/5" />
                                                <div className="h-3 bg-gray-200 rounded-md mb-6 w-3/4" />

                                                <div className="h-20 bg-gray-200 rounded-md mb-4" />
                                                <div className="h-3 bg-gray-200 rounded-md mb-2 w-5/6" />
                                                <div className="h-3 bg-gray-200 rounded-md mb-2" />
                                          </div>

                                          {/* Widget Button */}
                                          <div className="absolute bottom-4 right-4 bg-primary-500 text-white rounded-full p-3 shadow-lg animate-pulse-subtle cursor-pointer">
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                      <path d="M14 3V7C14 7.55228 14.4477 8 15 8H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                      <path d="M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H14L19 8V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                      <path d="M9 17H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                      <path d="M9 13H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                          </div>

                                          {/* Update Badge */}
                                          <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                                3
                                          </div>
                                    </div>

                                    {/* Tooltip */}
                                    <div className="absolute -top-16 right-0 bg-gray-900 text-white text-sm py-2 px-4 rounded-lg shadow-lg w-56 animate-pulse-subtle">
                                          {t('3_new_updates_available')}
                                          <div className="absolute bottom-0 right-6 transform translate-y-1/2 rotate-45 w-3 h-3 bg-gray-900"></div>
                                    </div>

                                    {/* Widget Panel (Slightly Visible) */}
                                    <div className="absolute -right-40 bottom-20 w-64 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden opacity-60">
                                          <div className="bg-primary-500 text-white p-3">
                                                <div className="font-medium">{t('recent_updates')}</div>
                                          </div>
                                          <div className="p-3">
                                                <div className="h-10 bg-gray-100 rounded-md mb-2"></div>
                                                <div className="h-10 bg-gray-100 rounded-md mb-2"></div>
                                                <div className="h-10 bg-gray-100 rounded-md"></div>
                                          </div>
                                    </div>
                              </div>
                        </div>
                  </div>
            </SectionPageLayout >
      );
};

export default Widget;
